import React, { useEffect, useState } from "react";
import { Grid, Tab, TabProps } from "semantic-ui-react";
import styles from "./styles.module.scss";
import CFReportViewer from "../CFReportViewer";
import VARReportViewer from "../VARReportViewer";
import MTMReportViewer from "../MTMReportViewer";
import { useLocation, useParams } from "react-router";
import AlertsViewer from "../AlertsViewer";
import InstrumentView from "../InstrumentView";
import ReportPicker from "../../components/ReportPicker";
import {
  TkrCusip,
  InstrumentParams,
  MtmItem,
  PortfolioEntry,
  Slice,
} from "../../types";
import { RootAction, RootState } from "StoreTypes";
import { apiToken } from "../../services/legit/token";
import { fetchMtmReport, fetchSlices } from "../../services/reports/selectors";
import { bindActionCreators, Dispatch as reduxDispatch } from "redux";
import { legit } from "apex-legit-service";
import { LEGIT_SERVICE_CONFIG } from "../../common";
import {
  instrumentRequest,
  reportsRequest,
  setSliceDate,
  setTkrCusip,
  slicesRequest,
} from "../../services/reports/actions";
import { connect } from "react-redux";
import ProjectedVARReportViewer from "containers/ProjectedVARReportViewer";

interface Props {
  token: string | null;
  reportDetails: Slice;
  slices: string[];
  fetchSlices: (date: string, token: string | null) => void;
  setSlice: (slice: Slice) => void;
  fetchInst: (params: InstrumentParams, token: string | null) => void;
  fetchReport: (reportDetails: Slice, token: string | null) => void;
  mtmReport: MtmItem;
  setTkrCusip: (tkrCusip: TkrCusip) => void;
}

interface ParamsTypes {
  date: string;
  sliceid: string;
  tkr: string;
  cusip: string;
}

const DetailedView = ({
  reportDetails,
  slices,
  fetchSlices,
  token,
  setSlice,
  fetchInst,
  fetchReport,
  mtmReport,
  setTkrCusip,
}: Props) => {
  const [index, setActiveIndex] = useState(1);
  const params = useParams<ParamsTypes>();

  const [cusipTkrCollection, setCusipTkrCollection] = useState<
    PortfolioEntry[]
  >([]);

  useEffect(() => {
    if (mtmReport.portfolioDiff != undefined) {
      setCusipTkrCollection(mtmReport.portfolioDiff);
    }
  }, [mtmReport.portfolioDiff]);

  const panes = [
    {
      menuItem: "CF",
      pane: {
        key: "cf",
        content: <CFReportViewer />,
      },
    },
    {
      menuItem: "VaR",
      pane: {
        key: "var",
        content: <VARReportViewer />,
      },
    },
    {
      menuItem: "Projected VaR",
      pane: {
        key: "apex-var",
        content: <ProjectedVARReportViewer />,
      },
    },
    {
      menuItem: "MtM",
      pane: {
        key: "mtm",
        content: <MTMReportViewer />,
      },
    },
    {
      menuItem: "Instrument",
      pane: {
        key: "instrument",
        content: <InstrumentView />,
      },
    },
    {
      key: "alerts",

      menuItem: "Alerts",
      pane: { key: "alerts", content: <AlertsViewer /> },
    },
  ];

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };

  const level = useQuery().get("level");

  useEffect(() => {
    const handleActiveIndex = (index: number) => {
      switch (level ? level.toLowerCase() : "") {
        case "cf":
          index = 0;
          break;
        case "var":
          index = 1;
          break;
        case "projected-var":
          index = 2;
          break;
        case "mtm":
          index = 3;
          break;
        case "instrument":
          index = 4;
          break;
        case "alerts":
          index = 5;
          break;
      }
      return index;
    };
    setActiveIndex(handleActiveIndex);
  }, [level]);

  useEffect(() => {
    if ((params?.tkr || params?.cusip) && params.date && params.sliceid) {
      fetchInst(
        {
          tkrCusip: {
            tkr: params?.tkr as string,
            cusip: params?.cusip as string,
          },
          date: params.date,
          slice: params.sliceid,
        },
        token
      );
    }
  }, [fetchInst, params, token]);

  const handleTabChange = (event: any, data: TabProps) => {
    setActiveIndex(
      data.activeIndex || data.activeIndex == 0 ? +data.activeIndex : index
    );
  };

  return (
    <div>
      <div className={styles.picker}>
        <ReportPicker
          date={reportDetails.date}
          slices={slices}
          currSlice={reportDetails.id}
          handleDateChange={fetchSlices}
          token={token}
          handleSliceChange={setSlice}
          cusipTkrCollection={cusipTkrCollection}
          setTkrCusip={setTkrCusip}
          fetchInst={fetchInst}
          fetchReport={fetchReport}
          tabIndex={index}
          urlBase={"/riskUI/reports"}
        />
      </div>
      <Grid>
        <Grid.Row columns={1}>
          <Grid divided="vertically">
            <div className={styles.tabs} id={"tabs"}>
              <Tab
                panes={panes}
                renderActiveOnly={false}
                activeIndex={index}
                onTabChange={handleTabChange}
              />
            </div>
          </Grid>
        </Grid.Row>
      </Grid>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  token: apiToken(state),
  slices: fetchSlices(state),
  mtmReport: fetchMtmReport(state),
});

const mapDispatchToProps = (dispatch: reduxDispatch<RootAction>) =>
  bindActionCreators(
    {
      logout: () => legit.logout(LEGIT_SERVICE_CONFIG, { apexUser: true }),
      fetchReport: (reportDetails, token) =>
        reportsRequest(reportDetails, token),
      fetchSlices: (date, token) => slicesRequest(date, token),
      setSlice: (reportDetails) => setSliceDate(reportDetails),
      fetchInst: (reportDetails, token) =>
        instrumentRequest(reportDetails, token),
      setTkrCusip: (tkrCusip) => setTkrCusip(tkrCusip),
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(DetailedView);
